import { styled, theme, utils } from 'ui/theme';
import { media } from 'ui/theme/utils';
import { Overlay, ButtonIcon } from 'ui/components/primitives';

const headerSize = {
  mobile: `calc(${theme.sizes[19]} + ${theme.space[16]})`,
  desktop: `calc(${theme.sizes[22]} + ${theme.space[22]})`,
  smallDesktop: `calc(${theme.sizes[14]} + ${theme.space[16]})`,
  smallMobile: `calc(${theme.sizes[4]} + ${theme.space[16]})`,
};

export const Header = {
  Drawer: styled.div`
    backface-visibility: hidden;
    bottom: 0px;
    left: 0px;
    position: fixed;
    top: 0px;
    transform: translateX(-100%);
    transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1) 0s;
    width: 80%;
    z-index: 800;

    &[data-open='true'] {
      box-shadow: ${theme.shadows.right};
      transform: translateX(0px);
    }
  `,

  Close: styled(ButtonIcon)`
    background-color: ${theme.colors.build.lightDark2};
    padding: ${theme.space[2]};
    position: absolute;
    right: calc(-1 * ${theme.space[9]} - 12px);
    top: ${theme.space[4]};

    &[data-show='false'] {
      visibility: hidden;
    }
  `,

  Overlay: styled(Overlay)`
    &[data-visible='true'] {
      z-index: 799;
    }
  `,

  Wrapper: styled.div`
    background-color: ${theme.colors.build.lightDark2};
    height: 100%;
  `,

  Component: styled.header`
    ${utils.flex.center};
    background-color: ${theme.colors.build.lightDark2};
    color: ${theme.colors.build.darkLight};
    flex-direction: column;
    padding: ${theme.space[0]};
    width: 100%;
    z-index: ${theme.zIndices.header};

    &[data-fixed='true'] {
      ${utils.position.fixedTo.top};
    }

    &[data-variant='black'] {
      background-color: ${theme.colors.build.darkLight};
      color: ${theme.colors.build.lightDark2};
    }

    ${utils.media.min('l')`
      min-height: ${headerSize.desktop};
    `}

    &[data-small='true'] {
      ${utils.media.min('l')`
        min-height: ${headerSize.smallDesktop};
      `}
    }
  `,

  Container: styled.div`
    ${utils.flex.center};
    ${utils.container}
    gap: ${theme.space[4]};
    padding: ${theme.space[2]} ${theme.space[5]};

    &[data-justify='space-between'] {
      justify-content: space-between;
    }

    &[aria-labelledby='sub-header'] {
      padding: ${theme.space[0]} ${theme.space[5]};
    }

    &[data-justify='center'] {
      justify-content: center;
    }

    ${media.min('xl')`
      gap: ${theme.space[8]};
      padding: 0 ${theme.space[4]};

      &[aria-labelledby='sub-header'] {
        padding: ${theme.space[2]} ${theme.space[4]};
      }
    `};
  `,

  NavBar: styled.nav`
    ${utils.flex.center};
    ${utils.alignX}
    ${utils.container}
    align-items: flex-start;
    background-color: ${theme.colors.build.lightDark2};
    flex-direction: column;
    font-size: ${theme.fontSizes.md};
    height: 100%;
    list-style: none;
    max-height: 100%;
    overflow: auto;
    padding: ${theme.space[4]} ${theme.space[4]} ${theme.space[23]} ${theme.space[4]};
    text-align: center;

    &[data-justify='right'] {
      justify-content: right;
    }

    ${utils.media.min('xl')`
      padding: 0;
      overflow: initial;
    `}

    ${utils.media.min('l')`
      flex-direction: row;
      gap: ${theme.space[4]};
      padding: 0;
      overflow: initial;
      min-height: 86px;
    `}

    & a[data-active='true'] {
      text-decoration: none;
    }

    & button {
      display: flex;
      flex-direction: column;
      padding: 0;

      > svg {
        color: ${theme.colors.action.navigation.standard.static};
        width: ${theme.sizes[6]};
      }

      > span > svg {
        color: ${theme.colors.action.navigation.standard.static};
        transition: all 0.4s ease;
        width: ${theme.sizes[7]};

        ${utils.media.min('l')`
          color: ${theme.colors.build.darkDark};
          width: ${theme.sizes[5]};
        `}
      }
    }

    li {
      display: flex;
      flex-direction: column;
      padding-bottom: ${theme.space[2]};
      position: relative;
      width: 100%;

      ${utils.media.min('l')`
        padding: ${theme.space[4]} ${theme.space[0]};
        width: auto;
      `}

      > a > button,
      > button {
        align-items: center;
        border-radius: ${theme.space[1]};
        display: flex;
        flex-direction: row;
        gap: 6px;
        padding: ${theme.space[3]} ${theme.space[2]};
        width: 100%;

        &.active {
          background-color: ${theme.colors.build.lightDark1};
          border-radius: ${theme.space[1]} ${theme.space[1]} 0 0;

          span svg {
            transform: rotate(180deg);
          }

          ${utils.media.min('l')`
            border-radius: ${theme.space[1]};
          `}
        }

        > span {
          align-items: center;
          color: ${theme.colors.build.darkDark};
          display: flex;
          font-size: ${theme.fontSizes.lg};
          gap: ${theme.space[1]};
          justify-content: space-between;
          justify-content: space-between;
          width: 100%;
        }
      }

      > a > button,
      > button {
        ${utils.media.min('l')`
          gap: 2px;
          flex-direction: column;
          padding: ${theme.space[1]} ${theme.space[2]};
        `}
      }

      > a {
        border-radius: ${theme.space[1]};
      }

      &.active > button {
        background-color: ${theme.colors.build.mediumLight2};

        > svg {
          color: ${theme.colors.build.brand1};
        }
      }

      &.active > a {
        background-color: ${theme.colors.build.mediumLight2};

        button svg {
          color: ${theme.colors.action.navigation.standard.static};
        }

        span svg {
          transform: rotate(180deg);
        }
      }

      ${utils.media.min('l')`
        &:hover {
          > a,
          > button {
            background-color: ${theme.colors.build.lightDark1};

            span svg {
              transform: rotate(180deg);
            }
          }

          > .dropdown {
            display: block;
          }
        }
      `}
    }
  `,

  Divider: styled.div`
    border-top: 1px solid ${theme.colors.build.lightDark1};
    height: 1px;
    width: 100%;
  `,

  Space: styled.div`
    padding-top: ${headerSize.mobile};

    ${utils.media.min('l')`
      padding-top: ${headerSize.desktop};
    `}

    &[data-small='true'] {
      padding-top: ${headerSize.smallMobile};

      ${utils.media.min('l')`
        padding-top: ${headerSize.smallDesktop};
      `}
    }
  `,

  Logout: styled.li`
    margin: ${theme.space[5]} 0 ${theme.space[9]};

    button {
      font-size: ${theme.fontSizes.md};
      height: ${theme.sizes[8]};
      margin: 0 auto;
      max-width: 142px;

      > svg {
        width: ${theme.sizes[3]};
      }
    }
  `,
};
